import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import { SUBSCRIPTION_EXPIRY_NOTICE } from 'modules/organizer/domain/constants/subscription-expiry-notice';
import { formatSubscriptionExpiryMessage } from 'modules/organizer/domain/utils/subscription-expiry-notice';
import { useGetActiveTournamentPlanQuery } from 'modules/organizer/store/organizer-plan-api';
import React from 'react';
import { useFetchActivePlan } from 'shared/hooks/fetchering/use-fetch-active-plan';
import UDCard from 'ud-ui/components/card';
import UDText from 'ud-ui/components/text';

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <UDText type="footnote" color="#686A79">
        {label}
      </UDText>
      <UDText type="subhead">{value}</UDText>
    </div>
  );
}

function Subscription() {
  const { data: subscription, isLoading } = useFetchActivePlan();
  const tournamentId = subscription?.tournamentId;
  const { data: plan, isLoading: isPlanLoading } = useGetActiveTournamentPlanQuery(
    { tournamentId: tournamentId as number },
    { skip: !tournamentId },
  );

  if (isLoading) {
    return <UDText type="body-text">Загрузка...</UDText>;
  }

  if (!subscription) {
    return (
      <UDCard className="max-w-md">
        <UDText type="headline" className="mb-2">
          Подписка не активна
        </UDText>
        <UDText type="body-text" color="#686A79">
          У вас нет активной платной подписки турнира.
        </UDText>
      </UDCard>
    );
  }

  const daysLeft = differenceInCalendarDays(parseISO(subscription.activeTo), new Date());
  const isExpiringSoon = daysLeft >= 0 && daysLeft <= SUBSCRIPTION_EXPIRY_NOTICE.warnWithinDays;
  const planName = isPlanLoading ? 'Загрузка...' : (plan?.name ?? subscription.purchaseType);

  return (
    <UDCard className="max-w-md">
      <div className="mb-4 flex items-center justify-between">
        <UDText className={'h-6'} type="headline">
          Текущая подписка
        </UDText>
        <span
          className={`rounded-full px-2 py-1 text-xs font-medium text-white ${isExpiringSoon ? 'bg-caseError' : 'bg-darkYellow-500'}`}
        >
          {isExpiringSoon ? 'Скоро истекает' : 'Активна'}
        </span>
      </div>
      <InfoRow label="Действует с" value={format(parseISO(subscription.activeFrom), 'dd.MM.yyyy')} />
      <InfoRow label="Действует до" value={format(parseISO(subscription.activeTo), 'dd.MM.yyyy')} />
      <InfoRow label="Тариф" value={planName} />
      {isExpiringSoon && (
        <UDText type="footnote" className="text-caseError mt-1 block">
          {formatSubscriptionExpiryMessage(daysLeft, subscription.activeTo)}
        </UDText>
      )}
    </UDCard>
  );
}

export default Subscription;
